import { formatPercentage } from '@toggle/helpers';
import * as d3 from 'd3';
import React, { FC, useId } from 'react';

import { getHumanFriendlyTicks } from '../helpers/chart-ticks/chart-ticks';
import * as Shared from '../SharedStyles.styles';
import {
  HighLowBar,
  HighLowMedianDataPoint,
} from './components/high-low-bar/HighLowBar';
import * as S from './HighLowChart.styles';

export interface HighLowChartProps {
  data: HighLowMedianDataPoint[];
  width: number;
  height: number;
  padding?: {
    left: number;
    right: number;
  };
  alt?: string;
  variant?: 'primary' | 'secondary';
}

const margin = {
  top: 20,
  right: 50,
  bottom: 30,
  left: 0,
};

const defaultPadding = {
  left: 85,
  right: 150,
};

export const HighLowChart: FC<HighLowChartProps> = ({
  data,
  width,
  height,
  padding = defaultPadding,
  alt,
  variant = 'primary',
}) => {
  const chartId = useId();
  const [xMin, xMax] = [0, data.length - 1];
  const yMax = d3.max(data, d => d.high) ?? 0;
  const yMin = d3.min(data, d => d.low) ?? 0;

  const { chartTicks, maxRounded, minRounded } = getHumanFriendlyTicks({
    min: yMin,
    max: yMax,
  });

  const xScale = d3
    .scaleLinear()
    .domain([xMin, xMax])
    .range([margin.left + padding.left, width - margin.left - padding.right]);

  const yScale = d3
    .scaleLinear()
    .domain([minRounded, maxRounded])
    .range([height - margin.bottom, margin.top]);

  return (
    <S.HighLowChartRoot>
      <Shared.RootSVG
        viewBox={`0 0 ${width} ${height}`}
        data-testid="high-low-chart"
      >
        {alt && <title>{alt}</title>}
        <g>
          {chartTicks.map(tickValue => (
            <g key={tickValue} transform={`translate(0, ${yScale(tickValue)})`}>
              <Shared.AxisLine
                x1={margin.left}
                x2={width - margin.right}
                strokeWidth={tickValue === 0 ? 2 : 1}
              />
              <Shared.AxisText
                textAnchor="end"
                x={width}
                alignmentBaseline="middle"
              >
                {formatPercentage(tickValue, {
                  suffix: '%',
                  decimals: variant === 'secondary' ? 0 : 2,
                })}
              </Shared.AxisText>
            </g>
          ))}
        </g>

        <g transform={`translate(0, ${height})`}>
          {data.map((item, index) => (
            <Shared.AxisText
              key={`${chartId}-${index}`}
              x={xScale(index)}
              textAnchor="middle"
            >
              {item.label}
            </Shared.AxisText>
          ))}
        </g>

        {variant === 'secondary' &&
          (() => {
            if (!data.length) {
              return null;
            }

            const createSegments = (values: number[]) => {
              const segments: { path: string; isAboveZero: boolean }[] = [];
              const y0 = yScale(0);

              for (let i = 0; i < values.length; i++) {
                const currentValue = values[i];
                const currentX = xScale(i);
                const currentY = yScale(currentValue);
                const currentIsAboveZero = currentValue > 0;

                if (i === 0) {
                  segments.push({
                    path: `M ${currentX} ${currentY}`,
                    isAboveZero: currentIsAboveZero,
                  });
                  continue;
                }

                const prevValue = values[i - 1];
                const prevX = xScale(i - 1);
                const prevIsAboveZero = prevValue > 0;

                if (prevIsAboveZero !== currentIsAboveZero) {
                  const t = -prevValue / (currentValue - prevValue);
                  const intersectionX = prevX + t * (currentX - prevX);

                  const lastSegment = segments[segments.length - 1];
                  lastSegment.path += ` L ${intersectionX} ${y0}`;

                  segments.push({
                    path: `M ${intersectionX} ${y0} L ${currentX} ${currentY}`,
                    isAboveZero: currentIsAboveZero,
                  });
                } else {
                  const lastSegment = segments[segments.length - 1];
                  lastSegment.path += ` L ${currentX} ${currentY}`;
                }
              }

              return segments;
            };

            const highSegments = createSegments(data.map(d => d.high));
            const lowSegments = createSegments(data.map(d => d.low));
            const medianSegments = createSegments(data.map(d => d.median));

            const y0 = yScale(0);
            const clipPathId = `${chartId}-area-clip`;

            const createClipPath = () => {
              const points: { x: number; y: number }[] = [];

              for (let i = 0; i < data.length; i++) {
                const currentHigh = data[i].high;
                const currentX = xScale(i);
                const currentHighY = yScale(currentHigh);

                if (i === 0) {
                  points.push({ x: currentX, y: currentHighY });
                } else {
                  const prevHigh = data[i - 1].high;
                  const prevX = xScale(i - 1);
                  const prevIsHighAboveZero = prevHigh > 0;
                  const currentIsHighAboveZero = currentHigh > 0;

                  if (prevIsHighAboveZero !== currentIsHighAboveZero) {
                    const t = -prevHigh / (currentHigh - prevHigh);
                    const intersectionX = prevX + t * (currentX - prevX);
                    points.push({ x: intersectionX, y: y0 });
                  }

                  points.push({ x: currentX, y: currentHighY });
                }
              }

              for (let i = data.length - 1; i >= 0; i--) {
                const currentLow = data[i].low;
                const currentX = xScale(i);
                const currentLowY = yScale(currentLow);

                if (i === data.length - 1) {
                  points.push({ x: currentX, y: currentLowY });
                } else {
                  const nextLow = data[i + 1].low;
                  const nextX = xScale(i + 1);
                  const currentIsLowAboveZero = currentLow > 0;
                  const nextIsLowAboveZero = nextLow > 0;

                  if (currentIsLowAboveZero !== nextIsLowAboveZero) {
                    const t = -currentLow / (nextLow - currentLow);
                    const intersectionX = currentX + t * (nextX - currentX);
                    points.push({ x: intersectionX, y: y0 });
                  }

                  points.push({ x: currentX, y: currentLowY });
                }
              }

              return `M ${points[0].x} ${points[0].y} ${points
                .slice(1)
                .map(p => `L ${p.x} ${p.y}`)
                .join(' ')} Z`;
            };

            const clipPath = createClipPath();

            return (
              <g key={`${chartId}-tracing-lines`}>
                <defs>
                  <clipPath id={clipPathId}>
                    <path d={clipPath} />
                  </clipPath>

                  <linearGradient
                    id={`${chartId}-green-gradient`}
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stopColor="var(--viz-bullish)"
                      stopOpacity="0.2"
                    />
                    <stop
                      offset="70%"
                      stopColor="var(--viz-bullish)"
                      stopOpacity="0.15"
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--viz-bullish)"
                      stopOpacity="0.1"
                    />
                  </linearGradient>

                  <linearGradient
                    id={`${chartId}-red-gradient`}
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stopColor="var(--viz-bearish)"
                      stopOpacity="0.2"
                    />
                    <stop
                      offset="70%"
                      stopColor="var(--viz-bearish)"
                      stopOpacity="0.15"
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--viz-bearish)"
                      stopOpacity="0.1"
                    />
                  </linearGradient>
                </defs>

                <Shared.AreaFill
                  d={`M ${margin.left} ${y0} L ${
                    width - margin.right
                  } ${y0} L ${width - margin.right} ${margin.top} L ${
                    margin.left
                  } ${margin.top} Z`}
                  $hasProfit
                  gradientId={`${chartId}-green-gradient`}
                  clipPath={`url(#${clipPathId})`}
                />

                <Shared.AreaFill
                  d={`M ${margin.left} ${y0} L ${
                    width - margin.right
                  } ${y0} L ${width - margin.right} ${
                    height - margin.bottom
                  } L ${margin.left} ${height - margin.bottom} Z`}
                  $hasProfit={false}
                  gradientId={`${chartId}-red-gradient`}
                  clipPath={`url(#${clipPathId})`}
                />

                {highSegments.map((segment, index) => (
                  <Shared.Line
                    key={`high-${index}`}
                    d={segment.path}
                    $hasProfit={segment.isAboveZero}
                    $strokeWidth={1}
                  />
                ))}

                {lowSegments.map((segment, index) => (
                  <Shared.Line
                    key={`low-${index}`}
                    d={segment.path}
                    $hasProfit={segment.isAboveZero}
                    $strokeWidth={1}
                  />
                ))}

                {medianSegments.map((segment, index) => (
                  <Shared.Line
                    key={`median-${index}`}
                    d={segment.path}
                    $hasProfit={segment.isAboveZero}
                    strokeDasharray="2"
                    $strokeWidth={1}
                  />
                ))}
              </g>
            );
          })()}

        {data.map((dataPoint, index) => {
          const yHigh = yScale(dataPoint.high);
          const yLow = yScale(dataPoint.low);
          const yMedian = yScale(dataPoint.median);
          const y0 = yScale(0);

          return (
            <g
              key={`${chartId}-${index}`}
              transform={`translate(${xScale(index)}, 0)`}
            >
              <HighLowBar
                yMedian={yMedian}
                yLow={yLow}
                yHigh={yHigh}
                y0={y0}
                dataPoint={dataPoint}
                showMedianLabel={variant === 'primary'}
                numberFormatter={value =>
                  formatPercentage(value, { suffix: '%' })
                }
              />
            </g>
          );
        })}
      </Shared.RootSVG>
    </S.HighLowChartRoot>
  );
};
